export type IncomeDrillMetric = 'gross' | 'discount' | 'tax' | 'net'

export interface IncomeInvoiceDrillDown {
  label: string
  metric: IncomeDrillMetric
  amount: string
  invoiceCount: number
  startsOn: string
  endsOn: string
  grouping: 'month' | 'origin' | 'membership_level'
  granularity: 'month' | 'quarter' | 'year' | null
  groupKey: string
}

export function incomeDrillDownParams(drillDown: IncomeInvoiceDrillDown) {
  const params = new URLSearchParams({
    income_label: drillDown.label,
    income_metric: drillDown.metric,
    income_amount: drillDown.amount,
    income_count: drillDown.invoiceCount.toString(),
    income_starts_on: drillDown.startsOn,
    income_ends_on: drillDown.endsOn,
    income_grouping: drillDown.grouping,
    income_group_key: drillDown.groupKey,
  })
  if (drillDown.granularity) params.set('income_granularity', drillDown.granularity)
  return params.toString()
}

export function incomeDrillDownPath(drillDown: IncomeInvoiceDrillDown) {
  return `/finance/invoices?${incomeDrillDownParams(drillDown)}`
}

export function incomeDrillDownFromSearch(search: string): IncomeInvoiceDrillDown | undefined {
  const params = new URLSearchParams(search)
  const label = params.get('income_label')
  const metric = params.get('income_metric')
  const amount = params.get('income_amount')
  const invoiceCount = Number(params.get('income_count'))
  const startsOn = params.get('income_starts_on')
  const endsOn = params.get('income_ends_on')
  const grouping = params.get('income_grouping')
  const granularity = params.get('income_granularity')
  const groupKey = params.get('income_group_key')
  if (
    !validDrillDown({
      label,
      metric,
      amount,
      invoiceCount,
      startsOn,
      endsOn,
      grouping,
      granularity,
      groupKey,
    })
  )
    return undefined

  return {
    label: label as string,
    metric: metric as IncomeDrillMetric,
    amount: amount as string,
    invoiceCount,
    startsOn: startsOn as string,
    endsOn: endsOn as string,
    grouping: grouping as IncomeInvoiceDrillDown['grouping'],
    granularity: granularity as IncomeInvoiceDrillDown['granularity'],
    groupKey: groupKey as string,
  }
}

function validDrillDown({
  label,
  metric,
  amount,
  invoiceCount,
  startsOn,
  endsOn,
  grouping,
  granularity,
  groupKey,
}: {
  label: string | null
  metric: string | null
  amount: string | null
  invoiceCount: number
  startsOn: string | null
  endsOn: string | null
  grouping: string | null
  granularity: string | null
  groupKey: string | null
}): boolean {
  return Boolean(
    label &&
    isIncomeMetric(metric) &&
    amount &&
    Number.isInteger(invoiceCount) &&
    invoiceCount >= 0 &&
    startsOn &&
    endsOn &&
    grouping &&
    ['month', 'origin', 'membership_level'].includes(grouping) &&
    (!granularity || ['month', 'quarter', 'year'].includes(granularity)) &&
    groupKey,
  )
}

function isIncomeMetric(value: string | null): value is IncomeDrillMetric {
  return value !== null && ['gross', 'discount', 'tax', 'net'].includes(value)
}
